// src/pages/EventDetailsPage.js
import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, Avatar, Chip } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { joinEvent, leaveEvent, repostEventToFeed } from '../features/collaboration/services/collaborationService';

const EventDetailsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendeeCount, setAttendeeCount] = useState(0);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'tm-events', eventId);
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          setEvent(null);
        } else {
          const data = { id: snap.id, ...(snap.data() || {}) };
          setEvent(data);
          setAttendeeCount((data.attendees || []).length);
          setJoined(currentUser ? (data.attendees || []).includes(currentUser.uid) : false);
        }
      } catch (err) {
        console.error('Failed to fetch event', err);
      } finally {
        setLoading(false);
      }
    };
    if (eventId) load();
  }, [eventId, currentUser]);

  const handleJoinToggle = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    try {
      if (joined) {
        await leaveEvent(eventId, currentUser.uid);
        setJoined(false);
        setAttendeeCount(c => Math.max(0, c - 1));
      } else {
        await joinEvent(eventId, currentUser.uid);
        setJoined(true);
        setAttendeeCount(c => c + 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRepost = async () => {
    if (!currentUser) return;
    try {
      await repostEventToFeed(currentUser.uid, event);
      // Optionally notify user
    } catch (err) {
      console.error('Repost failed', err);
    }
  };

  if (loading) return <Container sx={{ mt: 4 }}><Typography>Loading event…</Typography></Container>;
  if (!event) return <Container sx={{ mt: 4 }}><Typography>Event not found</Typography></Container>;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box display="flex" gap={3}>
        {event.bannerUrl && <Avatar variant="rounded" src={event.bannerUrl} sx={{ width: 200, height: 140 }} />}
        <Box>
          <Typography variant="h5" fontWeight="bold">{event.title}</Typography>
          <Typography variant="body2" color="textSecondary">{new Date(event.date).toLocaleString()}</Typography>
          <Typography variant="body2" color="textSecondary">{event.location}</Typography>
          <Typography sx={{ mt: 2 }}>{event.description}</Typography>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant={joined ? 'outlined' : 'contained'} onClick={handleJoinToggle}>
              {joined ? 'Leave Event' : 'Join Event'}
            </Button>

            <Button variant="text" onClick={handleRepost}>Repost to feed</Button>

            <Chip label={`${attendeeCount} attending`} />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default EventDetailsPage;
